import { Box, Paper, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import type { ReviewPeriodData } from "store/api/generatedApi";

interface PeriodGridProps {
  periods: ReviewPeriodData[];
  selectedPeriodIds: Set<number>;
  onTogglePeriod: (periodId: number, event?: React.MouseEvent) => void;
  getUserColor: (userId: string | null) => string;
}

export const PeriodGrid = ({
  periods,
  selectedPeriodIds,
  onTogglePeriod,
  getUserColor,
}: PeriodGridProps) => {
  const { t } = useTenantTranslation();

  const formatPeriodTime = (dateStr: string) =>
    dayjs(dateStr).utc().format("MMM D,HH:mm");

  return (
    <Paper sx={{ p: 2, flex: 1, overflow: "auto" }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
            gap: 1,
          }}
        >
          {periods.map((period) => (
            <Box
              key={period.id}
              onClick={(e) => {
                onTogglePeriod(period.id, e);
              }}
              sx={{
                p: 1,
                borderRadius: 1,
                cursor: "pointer",
                userSelect: "none",
                border: selectedPeriodIds.has(period.id)
                  ? "2px solid #1976d2"
                  : "2px solid transparent",
                backgroundColor: getUserColor(period.assigned_to_id),
                color: period.assigned_to_id ? "white" : "#666",
                transition: "all 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 2,
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  display: "block",
                  ...(period.assigned_to_id && {
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.5)",
                  }),
                }}
              >
                {formatPeriodTime(period.start)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.65rem",
                  opacity: 0.9,
                  display: "block",
                  ...(period.assigned_to_id && {
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.5)",
                  }),
                }}
              >
                {period.assigned_to_name ?? t("unassigned")}
              </Typography>
            </Box>
          ))}
        </Box>
      </Stack>
    </Paper>
  );
};
